import { ReactNode } from 'react';
import {
  DndContext, DragEndEvent, closestCenter, PointerSensor,
  useSensor, useSensors, KeyboardSensor,
} from '@dnd-kit/core';
import {
  arrayMove, SortableContext, sortableKeyboardCoordinates,
  useSortable, verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical } from 'lucide-react';

/**
 * Sortable list — kéo thả items theo chiều dọc.
 * Dùng cho block list, table columns, checklist items, signatures, etc.
 *
 * Usage:
 *   <SortableList
 *     items={blocks.map((b, i) => ({ id: 'block-' + i, data: b }))}
 *     onReorder={(newItems) => setBlocks(newItems.map(it => it.data))}
 *     renderItem={(item, dragHandle) => (
 *       <div>
 *         {dragHandle}
 *         {item.data.label}
 *       </div>
 *     )}
 *   />
 */
export interface SortableItem<T = any> {
  id: string;          // ID phải unique và stable
  data: T;
}

interface SortableListProps<T> {
  items: SortableItem<T>[];
  onReorder: (newItems: SortableItem<T>[]) => void;
  renderItem: (item: SortableItem<T>, dragHandle: ReactNode, index: number) => ReactNode;
  /** Disable drag-drop — fallback về list thường (không có handle) */
  disabled?: boolean;
}

export function SortableList<T>({ items, onReorder, renderItem, disabled }: SortableListProps<T>) {
  const sensors = useSensors(
    // Yêu cầu kéo 5px mới active drag — tránh trigger nhầm khi click button con
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = items.findIndex(i => i.id === active.id);
    const newIdx = items.findIndex(i => i.id === over.id);
    if (oldIdx < 0 || newIdx < 0) return;
    onReorder(arrayMove(items, oldIdx, newIdx));
  };

  if (disabled) {
    return (
      <>
        {items.map((it, i) => (
          <div key={it.id}>{renderItem(it, null, i)}</div>
        ))}
      </>
    );
  }

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={items.map(i => i.id)} strategy={verticalListSortingStrategy}>
        {items.map((it, i) => (
          <SortableRow key={it.id} item={it} index={i} renderItem={renderItem} />
        ))}
      </SortableContext>
    </DndContext>
  );
}

function SortableRow<T>({ item, index, renderItem }: {
  item: SortableItem<T>;
  index: number;
  renderItem: (item: SortableItem<T>, dragHandle: ReactNode, index: number) => ReactNode;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.id });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 10 : 'auto',
  };

  // Drag handle: chỉ vùng này kích hoạt drag (không phải toàn bộ row)
  const dragHandle = (
    <span
      {...attributes}
      {...listeners}
      style={{
        cursor: 'grab', display: 'inline-flex', alignItems: 'center',
        padding: '0 4px', color: '#bfbfbf', userSelect: 'none',
      }}
      title="Kéo để sắp xếp"
      onClick={(e) => e.stopPropagation()}
    >
      <GripVertical size={14} />
    </span>
  );

  return (
    <div ref={setNodeRef} style={style}>
      {renderItem(item, dragHandle, index)}
    </div>
  );
}
